import React from "react";
import { Grid, Card, CardContent, Box, Typography } from "@mui/material";
import {
  LocalOffer,
  Store as StoreIcon,
  ArrowForward,
} from "@mui/icons-material";

interface QuickActionCardData {
  icon: React.ReactNode;
  count: number;
  title: string;
  subtitle: string;
  gradient: string;
  onClick: () => void;
}

interface MallHubQuickActionsProps {
  totalOffers: number;
  totalStores: number;
  totalEvents: number;
  onViewOffers: () => void;
  onViewStores: () => void;
  onViewEvents: () => void;
}

/**
 * MallHubQuickActions Component
 *
 * Displays quick action cards for Offers, Events, and Stores
 */
const MallHubQuickActions: React.FC<MallHubQuickActionsProps> = ({
  totalOffers,
  totalStores,
  totalEvents,
  onViewOffers,
  onViewStores,
  onViewEvents,
}) => {
  const hasEvents = totalEvents > 0;
  const hasOffers = totalOffers > 0;
  const hasStores = totalStores > 0;

  const cards: QuickActionCardData[] = [
    ...(hasOffers
      ? [
          {
            icon: <LocalOffer sx={{ fontSize: 32 }} />,
            count: totalOffers,
            title: "Active Offers",
            subtitle: "Exclusive deals and discounts",
            gradient: "linear-gradient(135deg, #e67e22 0%, #f39c12 100%)", // Orange (unified)
            onClick: onViewOffers,
          },
        ]
      : []),
    ...(hasEvents
      ? [
          {
            icon: <span style={{ fontSize: 32 }}>🎉</span>,
            count: totalEvents,
            title: "Upcoming Events",
            subtitle: "Exciting happenings & activities",
            gradient: "linear-gradient(135deg, #8e44ad 0%, #c39bd3 100%)", // Purple (unified)
            onClick: onViewEvents,
          },
        ]
      : []),
    ...(hasStores
      ? [
          {
            icon: <StoreIcon sx={{ fontSize: 32 }} />,
            count: totalStores,
            title: "Stores",
            subtitle: "Browse all brands and shops",
            gradient: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)", // Navy (premium)
            onClick: onViewStores,
          },
        ]
      : []),
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 6 }}>
      {cards.map((card, index) => (
        <Grid item xs={12} md={hasEvents ? 4 : 6} key={index}>
          <Card
            elevation={0}
            sx={{
              background: card.gradient,
              color: "white",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "100%",
              borderRadius: { xs: 2.5, md: 3 },
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                opacity: 0.6,
              },
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                "&::before": {
                  opacity: 1,
                },
              },
            }}
            onClick={card.onClick}
          >
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              {/* MOBILE VIEW - Horizontal compact layout */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.isValidElement(card.icon) &&
                    card.icon.type !== "span" ? (
                      React.cloneElement(card.icon as React.ReactElement, {
                        sx: { fontSize: 20, color: "white" },
                      })
                    ) : (
                      <span style={{ fontSize: "20px" }}>{card.icon}</span>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ fontSize: "1.5rem", lineHeight: 1.2 }}
                      >
                        {card.count}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="600"
                        sx={{ opacity: 0.95, fontSize: "1rem" }}
                      >
                        {card.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.85, fontSize: "0.875rem" }}
                    >
                      {card.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForward sx={{ fontSize: 20, opacity: 0.7, ml: 0.5 }} />
              </Box>

              {/* DESKTOP VIEW - Horizontal layout with icon beside content */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2.5,
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.isValidElement(card.icon) &&
                    card.icon.type !== "span" ? (
                      React.cloneElement(card.icon as React.ReactElement, {
                        sx: { fontSize: 28, color: "white" },
                      })
                    ) : (
                      <span style={{ fontSize: "28px" }}>{card.icon}</span>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1.5,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          fontSize: { md: "1.5rem", lg: "1.75rem" },
                          lineHeight: 1.2,
                        }}
                      >
                        {card.count}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        sx={{
                          opacity: 0.95,
                          fontSize: { md: "1rem", lg: "1.125rem" },
                          lineHeight: 1.2,
                        }}
                      >
                        {card.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        opacity: 0.85,
                        fontSize: { md: "0.875rem", lg: "0.938rem" },
                      }}
                    >
                      {card.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForward sx={{ fontSize: 28, opacity: 0.7, ml: 1 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default MallHubQuickActions;
